import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getAuthToken } from "../../../utils/getAuthToken";

const API_BASE = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";

export const fetchQuickReplies = createAsyncThunk(
  "quickReplies/fetch",
  async (workspaceId, { rejectWithValue }) => {
    try {
      const token = await getAuthToken();
      const res = await fetch(`${API_BASE}/api/workspaces/${workspaceId}/quick-replies`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to load");
      return data.quickReplies;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const createQuickReply = createAsyncThunk(
  "quickReplies/create",
  async ({ workspaceId, name, message }, { rejectWithValue }) => {
    try {
      const token = await getAuthToken();
      const res = await fetch(`${API_BASE}/api/workspaces/${workspaceId}/quick-replies`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name, message }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to create");
      return data.quickReply;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const updateQuickReply = createAsyncThunk(
  "quickReplies/update",
  async ({ workspaceId, replyId, name, message }, { rejectWithValue }) => {
    try {
      const token = await getAuthToken();
      const res = await fetch(`${API_BASE}/api/workspaces/${workspaceId}/quick-replies/${replyId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name, message }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to update");
      return data.quickReply;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const deleteQuickReply = createAsyncThunk(
  "quickReplies/delete",
  async ({ workspaceId, replyId }, { rejectWithValue }) => {
    try {
      const token = await getAuthToken();
      const res = await fetch(`${API_BASE}/api/workspaces/${workspaceId}/quick-replies/${replyId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to delete");
      return replyId;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

const quickRepliesSlice = createSlice({
  name: "quickReplies",
  initialState: {
    items: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchQuickReplies.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchQuickReplies.fulfilled, (state, action) => { state.loading = false; state.items = action.payload; })
      .addCase(fetchQuickReplies.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(createQuickReply.fulfilled, (state, action) => {
        state.items = [...state.items, action.payload].sort((a, b) => a.name.localeCompare(b.name));
      })
      .addCase(updateQuickReply.fulfilled, (state, action) => {
        state.items = state.items
          .map((r) => (r._id === action.payload._id ? action.payload : r))
          .sort((a, b) => a.name.localeCompare(b.name));
      })
      .addCase(deleteQuickReply.fulfilled, (state, action) => {
        state.items = state.items.filter((r) => r._id !== action.payload);
      });
  },
});

export default quickRepliesSlice.reducer;
